import {
  CaseUpdateProviderResource,
  InboundWebhookProviderResource,
  IntegrationProviderResource,
  ManualReviewProviderResource,
} from "src/api/connectApi/types";
import {
  CONNECTIONS_MAP,
  providerInConfigMap,
} from "src/connections/connectionConfigs";
import { ResourceConfig } from "src/connections/connectionConfigs/types";
import { assertUnreachable } from "src/utils/typeUtils";

export const DOCS_LINK = "https://docs.taktile.com/";
export const DOCS_REPORT_CRITICAL_INCIDENT =
  "https://docs.taktile.com/references/report-critical-incident";
export const DOCS_FUNDAMENTALS_VERSIONS_AND_PUBLISHING =
  "https://docs.taktile.com/versioning-and-deployment/decision-flow-versions-and-publishing";
export const DOCS_FUNDAMENTALS_DATASET_TESTING =
  "https://docs.taktile.com/testing-and-mocking/datasets-and-testing-1";
export const DOCS_FUNDAMENTALS_SCHEMAS =
  "https://docs.taktile.com/decision-flow-fundamentals/input-output-schema-for-flows";
export const DOCS_FUNDAMENTALS_PARAMETERS =
  "https://docs.taktile.com/decision-flow-fundamentals/flow-parameters";
export const DOCS_FUNDAMENTALS_DECISION_HISTORY =
  "https://docs.taktile.com/monitoring/decision-history";
export const DOCS_RETENTION_POLICIES =
  "https://docs.taktile.com/settings-and-administration/data-retention-policies";
export const DOCS_API_KEYS = "https://docs.taktile.com/developer-docs/api-keys";
export const DOCS_JOBS = "https://docs.taktile.com/job-management/jobs";
export const DOCS_COMPARATIVE_ANALYTICS =
  "https://docs.taktile.com/testing-and-mocking/datasets-and-testing-1/analytics-and-metrics#comparative-analytics";
export const DOCS_AI_NODE =
  "https://docs.taktile.com/decision-flow-fundamentals/node-types/ai-node";
export const DOCS_CASE_ANALYTICS =
  "https://docs.taktile.com/case-management/case-analytics";
export const DOCS_FLOW_INSIGHTS =
  "https://docs.taktile.com/monitoring/insights";

export const getConnectDocsLink = (
  providerResource:
    | IntegrationProviderResource
    | InboundWebhookProviderResource
    | ManualReviewProviderResource
    | CaseUpdateProviderResource,
): string | null => {
  if (providerInConfigMap(providerResource.provider)) {
    const connectionConfig = CONNECTIONS_MAP[providerResource.provider];
    if (providerResource.resource in connectionConfig.resources) {
      const resourceConfig = connectionConfig.resources[
        providerResource.resource as keyof typeof connectionConfig.resources
      ] as ResourceConfig;
      return resourceConfig.externalLink;
    }
  }

  switch (providerResource.provider) {
    case "schufa": {
      switch (providerResource.resource) {
        case "b2c_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#b2c-report-credit-enquiry-bonitatsauskunft";
        }
        case "schufa_b2c_basic_account_and_exemption_from_execution_account_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#b2c-report-basic-and-exemption-from-execution-account-report";
        }
        case "schufa_b2c_monitor": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#b2c-report-monitor";
        }
        case "schufa_fraud_pre_check": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#fraud-pre-check";
        }
        case "b2b_report_cascading": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#b2b-credit-report-cascading-kurzauskunft-mit-kaskade";
        }
        case "schufa_b2c_furnish": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#b2c-report-furnish";
        }
        case "schufa_b2c_manual_retrieval": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#b2c-report-manual-retrieval";
        }
        case "schufa_b2c_update_address": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/schufa#b2c-report-update-address";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "boniversum": {
      if (providerResource.resource === "consumer_report") {
        return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/boniversum#consumer-report";
      } else {
        assertUnreachable(providerResource);
        return null;
      }
    }
    case "dnb": {
      switch (providerResource.resource) {
        case "identity_resolution": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/dun-and-bradstreet#identity-resolution";
        }
        case "credit_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/dun-and-bradstreet#credit-report";
        }
        case "custom_report": {
          return null;
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "codat": {
      switch (providerResource.resource) {
        case "company_identification": {
          return "https://docs.taktile.com/integrations/third-party-connections/open-banking/codat#company-identification";
        }
        case "company_info": {
          return "https://docs.taktile.com/integrations/third-party-connections/open-banking/codat#company-info";
        }
        case "enhanced_profit_and_loss_accounts": {
          return "https://docs.taktile.com/integrations/third-party-connections/open-banking/codat#enhanced-profit-and-loss-accounts";
        }
        case "enhanced_balance_sheet_accounts": {
          return "https://docs.taktile.com/integrations/third-party-connections/open-banking/codat#enhanced-balance-sheet-accounts";
        }
        case "aged_creditors_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/open-banking/codat#aged-creditors";
        }
        case "aged_debtors_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/open-banking/codat#aged-debtors";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "crif_b2c": {
      switch (providerResource.resource) {
        case "credit_check": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/crif#credit-check";
        }
        case "identity_check": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/crif#identity-check";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "crif_b2b": {
      switch (providerResource.resource) {
        case "credit_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/crif#credit-report";
        }
        case "full_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/crif#full-report";
        }
        case "search_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/crif#search-report";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "crif_high_mark": {
      return null;
    }
    case "equifax_uk": {
      if (providerResource.resource === "gateway") {
        return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/equifax-uk#gateway-report";
      } else {
        assertUnreachable(providerResource);
        return null;
      }
    }
    case "data_x": {
      if (providerResource.resource === "data_x_credit_report") {
        return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/datax-equifax#credit-report";
      } else {
        assertUnreachable(providerResource);
        return null;
      }
    }
    case "equifax_us": {
      switch (providerResource.resource) {
        case "scores_and_attributes": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/equifax-us#scores-and-attributes";
        }
        case "mla_report": {
          return null;
        }
        case "one_view_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/equifax-us#oneview-consumer-credit-report";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "equifax_us_commercial_risk": {
      if (
        providerResource.resource === "equifax_us_commercial_risk_credit_report"
      ) {
        return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/equifax-us/equifax-commercial-risk";
      } else {
        assertUnreachable(providerResource);
        return null;
      }
    }
    case "experian_us_b2b": {
      switch (providerResource.resource) {
        case "experian_us_b2b_business_search": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/experian#business-search-report";
        }
        case "experian_us_b2b_small_business_credit_share_scores": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/experian#small-business-credit-share-scores-report";
        }
        case "experian_us_b2b_small_business_credit_share_profiles_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/experian#small-business-credit-share-scores-profiles-report";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "experian_us_clarity_services": {
      if (providerResource.resource === "experian_us_clarity_services_report") {
        return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/experian/experian-clarity-services";
      } else {
        assertUnreachable(providerResource);
        return null;
      }
    }
    case "creditsafe": {
      switch (providerResource.resource) {
        case "company_credit_report": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/creditsafe#company-credit-report";
        }
        case "company_search": {
          return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/creditsafe#company-search";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    }
    case "experian_us": {
      if (providerResource.resource === "experian_us_credit_report") {
        return "https://docs.taktile.com/integrations/third-party-connections/credit-bureaus/experian#credit-report";
      } else {
        assertUnreachable(providerResource);
        return null;
      }
    }
    case "webhook":
      switch (providerResource.resource) {
        case "event": {
          return "";
        }
        case "review_event": {
          return "https://docs.taktile.com/decision-flow-fundamentals/manual-review";
        }
        case "case_update_event": {
          return "https://docs.taktile.com/decision-flow-fundamentals/node-types/case-action-nodes#update-a-case";
        }
        default:
          assertUnreachable(providerResource);
          return null;
      }
    default:
      // Unclear why we expect here error, this should not happen
      // @ts-expect-error - if we expect error, then we do not need to assertUnreachable
      assertUnreachable(providerResource);
      return null;
  }
};
